/** Ce que lit {@link hideBasemapExtrusions} d'une carte MapLibre. */
export interface ExtrusionHost {
  getStyle(): { layers?: { id: string; type: string }[] } | undefined;
  getLayer(id: string): unknown;
  getLayoutProperty(id: string, prop: string): unknown;
  setLayoutProperty(id: string, prop: string, value: string): unknown;
}

/**
 * Masque les bâtiments extrudés du fond de carte (`fill-extrusion`, ex. « Building 3D » de MapTiler
 * streets-v2) tant que le modèle 3D du site est affiché : leur toit, plus haut que les étages du
 * modèle, le cachait là où les emprises se recouvrent (Castelnau, 2026-10-28).
 *
 * @returns une fonction qui rétablit exactement la visibilité d'avant
 */
export function hideBasemapExtrusions(map: ExtrusionHost): () => void {
  const hidden: string[] = [];
  for (const layer of map.getStyle()?.layers ?? []) {
    if (layer.type === 'fill-extrusion' && map.getLayoutProperty(layer.id, 'visibility') !== 'none') {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
      hidden.push(layer.id);
    }
  }
  return () => {
    for (const id of hidden) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', 'visible');
      }
    }
  };
}

/** {@link ExtrusionHost} qui signale ses changements de style. */
export interface RestylableExtrusionHost extends ExtrusionHost {
  on(type: 'styledata', listener: () => void): unknown;
  off(type: 'styledata', listener: () => void): unknown;
}

/**
 * {@link hideBasemapExtrusions}, puis de nouveau à chaque changement de style : passer en satellite
 * puis revenir en vue rue ramène les bâtiments extrudés du fond MapTiler.
 *
 * @returns une fonction qui rétablit tout ce qui a été masqué et arrête le suivi
 */
export function keepBasemapExtrusionsHidden(map: RestylableExtrusionHost): () => void {
  const restores = [hideBasemapExtrusions(map)];
  const onStyle = () => {
    restores.push(hideBasemapExtrusions(map));
  };
  map.on('styledata', onStyle);
  return () => {
    map.off('styledata', onStyle);
    restores.splice(0).forEach(restore => restore());
  };
}
